import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate, type NavigateFunction } from 'react-router-dom';

import { getAccessToken } from '@/api/token';
import { useCurrentUser } from '@/features/auth/hooks/use-current-user';
import { BUG_REPORTS_KEYS } from '@/features/bug-reports/constants';
import { useInboxStore } from '@/features/inbox/state/inbox-ui.store';

import { notificationsKeys } from '../api/notifications.keys';
import { showNotificationToast } from '../components/show-notification-toast';
import {
    NOTIFICATION_REFERENCE_TYPES,
    NOTIFICATION_WS_EVENTS,
    type INotification,
    type IWsEnvelope,
    type IWsNewPayload,
} from '../types';
import { createNotificationsSocket } from '../ws/socket-client';
import { useMarkNotificationRead } from './useNotifications';

interface SocketContext {
    navigate: NavigateFunction;
    markReadMutate: (id: string) => void;
    currentUserId: string | undefined;
}

/**
 * True when the user is currently viewing this inbound notification's
 * conversation AND channel (per-channel) — in which case we suppress the
 * arrival toast and silently mark it read instead. An inbound on a different
 * channel of the same contact (e.g. WhatsApp while SMS is open) is not matched
 * and still surfaces.
 */
function isViewingNotifiedConversation(notification: INotification): boolean {
    if (notification.category !== 'INBOUND_MESSAGE' && notification.category !== 'CAMPAIGN_REPLY') {
        return false;
    }
    const { activeUnifiedConversationId, selectedComposerChannel } = useInboxStore.getState();
    if (!activeUnifiedConversationId || !selectedComposerChannel) return false;
    if (notification.referenceId !== activeUnifiedConversationId) return false;
    const channel = notification.metadata?.['channel'];
    return typeof channel === 'string' && channel === selectedComposerChannel;
}

/**
 * A notification is advisory for its OWN queries and for whatever it points at.
 *
 * The case that forces this: the support team replies to a bug report while the
 * reporter has that very thread open. The arrival is announced here and nowhere
 * else — the thread is plain React Query with no socket of its own — so without
 * this the reply the reporter was just told about would sit invisible behind a
 * cached thread until the API's 5-minute staleness window let a refetch through.
 *
 * The list is invalidated alongside the thread because a reply can also move the
 * report's status (replying to a closed one reopens it), which the row's badge
 * shows.
 */
function invalidateReferencedFeature(queryClient: QueryClient, notification: INotification): void {
    if (notification.referenceType !== NOTIFICATION_REFERENCE_TYPES.BUG_REPORT) return;

    void queryClient.invalidateQueries({ queryKey: BUG_REPORTS_KEYS.lists() });
    if (notification.referenceId) {
        void queryClient.invalidateQueries({
            queryKey: BUG_REPORTS_KEYS.detail(notification.referenceId),
        });
    }
}

/**
 * Connects to `/notifications` for the lifetime of the mounting component (the
 * bell, always mounted while authenticated). Keeps the notification queries
 * fresh (WS is an advisory signal; REST is the source of truth) and, on a NEW
 * notification, pops a top-right arrival toast — unless the current user is the
 * actor (self-suppression). Events are deduped by `eventId`.
 *
 * `navigate` / `markRead` / `currentUserId` live in a ref so the socket effect
 * stays `[queryClient]`-stable and doesn't reconnect on every render.
 */
export function useNotificationsSocket(): void {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const markRead = useMarkNotificationRead();
    const { data: currentUser } = useCurrentUser();

    const ctxRef = useRef<SocketContext>({
        navigate,
        markReadMutate: markRead.mutate,
        currentUserId: currentUser?.id,
    });
    useEffect(() => {
        ctxRef.current = {
            navigate,
            markReadMutate: markRead.mutate,
            currentUserId: currentUser?.id,
        };
    });

    useEffect(() => {
        const token = getAccessToken();
        if (!token) return;

        const socket = createNotificationsSocket(token);
        const seen = new Set<string>();

        const alreadySeen = (eventId: string | undefined): boolean => {
            if (!eventId) return false;
            if (seen.has(eventId)) return true;
            seen.add(eventId);
            return false;
        };

        const invalidate = (): void => {
            void queryClient.invalidateQueries({ queryKey: notificationsKeys.all });
        };

        const onNew = (envelope: IWsEnvelope<IWsNewPayload>): void => {
            if (alreadySeen(envelope?.eventId)) return;

            const notification = envelope?.payload?.notification;
            if (!notification) {
                invalidate();
                return;
            }

            // Actively viewing this conversation + channel → mark read silently,
            // no toast (markRead's onSuccess invalidates the queries).
            if (isViewingNotifiedConversation(notification)) {
                ctxRef.current.markReadMutate(notification.id);
                return;
            }

            invalidate();
            invalidateReferencedFeature(queryClient, notification);

            // Self-suppression: don't toast the actor for their own action.
            if (
                notification.actorUserId &&
                notification.actorUserId === ctxRef.current.currentUserId
            ) {
                return;
            }

            showNotificationToast(notification, {
                onNavigate: (url) => ctxRef.current.navigate(url),
                onMarkRead: (id) => ctxRef.current.markReadMutate(id),
            });
        };

        const onRead = (envelope: IWsEnvelope<unknown>): void => {
            if (alreadySeen(envelope?.eventId)) return;
            invalidate();
        };

        socket.on(NOTIFICATION_WS_EVENTS.NEW, onNew);
        socket.on(NOTIFICATION_WS_EVENTS.READ, onRead);
        socket.on(NOTIFICATION_WS_EVENTS.READ_MANY, onRead);
        socket.on(NOTIFICATION_WS_EVENTS.READ_ALL, onRead);
        socket.io.on('reconnect', invalidate);
        socket.connect();

        return () => {
            socket.off(NOTIFICATION_WS_EVENTS.NEW, onNew);
            socket.off(NOTIFICATION_WS_EVENTS.READ, onRead);
            socket.off(NOTIFICATION_WS_EVENTS.READ_MANY, onRead);
            socket.off(NOTIFICATION_WS_EVENTS.READ_ALL, onRead);
            socket.io.off('reconnect', invalidate);
            socket.disconnect();
        };
    }, [queryClient]);
}
